import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@material-ui/core";
import { SKButton, SKCard, SKCardContent, SKCardHeader, SKChip, SKList, SKListItem, SKTypography } from "../ui";
import { Pagination } from "@material-ui/lab";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import React, { useCallback, useContext, useMemo, useState } from "react";
import useFillStore from "../../hooks/useFillStore";
import useFormatNumber from "../../hooks/useFormatNumber";
import { StoreContext } from "../../store";
import { capitalizeFirstLetter } from "../../utils/formatting/string";
import NoDataFound from "../misc/NoData";
import { CheckCircleOutline, History, InfoOutlined, OpenInBrowser, OpenInNew, Receipt } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0.5),
  },
  cardBase: {
    height: "100%",
    // borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  hoverableCard: {
    backgroundColor: "#fff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 20px rgba(0,0,0,0.06)",
      borderColor: theme.palette.primary.main,
    },
  },
  staticCard: {
    backgroundColor: "#fafafa",
    boxShadow: "none",
  },
  cardHeader: {
    padding: theme.spacing(1.5, 2, 0, 2),
  },
  cardHeaderAction: {
    margin: 0,
  },
  cardTitle: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.02em",
  },
  cardContent: {
    padding: theme.spacing(2),
    "&:last-child": {
      paddingBottom: theme.spacing(2),
    },
  },
  planName: {
    fontSize: "1.25rem",
    fontWeight: 800,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  planPrice: {
    fontSize: "1rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "baseline",
    gap: 4,
    marginTop: theme.spacing(0.5),
  },
  priceInterval: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  statusChip: {
    height: 20,
    borderRadius: 4,
    fontWeight: 700,
    fontSize: "0.65rem",
    textTransform: "uppercase",
  },
  activeChip: {
    backgroundColor: "#E8F5E9",
    color: "#2E7D32",
    border: "1px solid #C8E6C9",
  },
  pendingChip: {
    backgroundColor: "#FFF3E0",
    color: "#EF6C00",
    border: "1px solid #FFE0B2",
  },
  usageSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#fff",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  usageLabel: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.75),
  },
  progressBarRoot: {
    height: 6,
    borderRadius: 3,
    backgroundColor: "#F0F0F0",
    overflow: "hidden",
  },
  progressBarValue: {
    height: "100%",
    borderRadius: 3,
    backgroundColor: "#FFD140",
  },
  infoList: {
    marginTop: theme.spacing(1.5),
    padding: 0,
    listStyle: "none",
  },
  infoItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px dashed ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  infoIcon: {
    color: theme.palette.text.disabled,
    fontSize: "1rem",
  },
  infoText: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  infoValue: {
    fontSize: "0.8rem",
    fontWeight: 600,
    marginLeft: "auto",
  },
  pendingContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  changeCard: {
    border: `1px solid ${theme.palette.warning.light}`,
    backgroundColor: "#FFFDE7",
    padding: theme.spacing(1.5),
    borderRadius: 8,
  },
  kpiValue: { fontWeight: 800, fontSize: 24 },
  activeInvoiceHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(1.5),
  },
  invoiceDetailList: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: "#fcfcfc",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(0.5, 0),
    "&:not(:last-child)": {
      borderBottom: `1px dashed ${theme.palette.divider}`,
    },
  },
  detailLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  detailValue: {
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  totalRow: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `2px solid ${theme.palette.divider}`,
  },
  totalLabel: {
    fontSize: "0.85rem",
    fontWeight: 800,
    color: theme.palette.text.primary,
  },
  totalValue: {
    fontSize: "1rem",
    fontWeight: 900,
    color: theme.palette.primary.main,
  },
}));

async function fetchData(store) {
  const results = await Promise.all([
    store.subscription.fetchOne(store.subscription.selected?.id),
    store.subscription.fetchInvoices(store.subscription.selected.id, {
      page: 1,
      limit: store.subscription.invoice.pagination.limit,
    }),
    store.subscription.fetchActiveInvoice(store.subscription.selected.id, {
      changeIds: store.subscription.change?.pending ? [store.subscription.change.pending.id] : [],
    }),
  ]);

  return results;
}

const BillingPanel = ({ allowExport = false }) => {
  const { t } = useTranslation("common");
  const formatNumber = useFormatNumber();
  const router = useRouter();

  const classes = useStyles();
  const store = useContext(StoreContext);
  const [isGenerating, setIsGenerating] = useState(false);

  const [fetching] = useFillStore(fetchData);

  // States for discount tests
  const [showModalDiscount, setShowModalDiscount] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);

  const { selected, invoice } = store.subscription;
  const missingPaymentLink =
    !invoice?.active?.external?.paymentUrl || moment().isAfter(moment(invoice?.active?.external?.expiryDate));

  const handleRegeneratePaymentLink = async () => {
    setIsGenerating(true);
    try {
      const { status, data } = await store.subscription.regeneratePaymentLink(selected.id, invoice.active.id);
      if (status === 200) {
        store.pushToastMessage({ message: t("Payment link regenerated successfully"), severity: "success" });
      } else {
        store.pushToastMessage({
          message: t("Failed to regenerate payment link. Please try again"),
          severity: "error",
        });
      }
    } finally {
      // store.subscription.fetchActiveInvoice(store.subscription.selected.id, {
      //   changeIds: store.subscription.change?.pending ? [store.subscription.change.pending.id] : [],
      // });
      setIsGenerating(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setIsApplyingDiscount(true);
    try {
      const { status } = await store.subscription.applyInvoiceDiscount(selected.id, invoice.active.id, discountCode);
      if (status === 200) {
        store.pushToastMessage({ message: t("Discount applied successfully"), severity: "success" });
        setDiscountCode("");
        setShowModalDiscount(false);
      } else {
        store.pushToastMessage({ message: t("Failed to apply discount code"), severity: "error" });
      }
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  const handleRemoveDiscount = useCallback(async () => {
    try {
      const { status } = await store.subscription.removeInvoiceDiscount(selected.id, invoice.active.id);
      if (status === 200) {
        store.pushToastMessage({ message: t("Discount removed successfully"), severity: "success" });
      } else {
        store.pushToastMessage({ message: t("Failed to remove discount"), severity: "error" });
      }
    } catch (e) {
      // console.error(e);
      store.pushToastMessage({ message: t("Failed to remove discount"), severity: "error" });
    }
  }, [store, selected, invoice, t]);

  return (
    <React.Fragment>
      <Box className={classes.root}>
        {/* First Section: Active / Pending Invoice */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <SKCard variant="static">
              <SKCardHeader title={t("Active Billing")} />
              <SKCardContent>
                {invoice?.active ? (
                  <Box>
                    <Box className={classes.activeInvoiceHeader}>
                      <Box>
                        <SKTypography variant="h6" style={{ fontWeight: 800, fontSize: "1.1rem" }}>
                          {invoice?.active.invoiceNumber}
                        </SKTypography>
                        <SKTypography variant="caption" subVariant="caption" display="block">
                          {invoice?.active.lineItems[0].description}
                        </SKTypography>
                      </Box>
                      <SKChip
                        label={capitalizeFirstLetter(invoice?.active.status || "Pending")}
                        variant={invoice?.active.status === "paid" ? "success" : "pending"}
                        className={classes.statusChip}
                      />
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <SKTypography variant="caption" subVariant="caption">
                          {/* {t("Due by")} */}
                          {t("Created on")}
                        </SKTypography>
                        <SKTypography variant="body2" style={{ fontWeight: 600 }}>
                          {/* {moment(invoice?.active.dueAt).format("LL")} */}
                          {moment(invoice?.active.createdAt).format("LL")}
                        </SKTypography>
                      </Grid>
                      {invoice?.active?.external?.expiryDate && (
                        <Grid item xs={6}>
                          <SKTypography variant="caption" subVariant="caption">
                            {t("Payment Link Expiry")}
                          </SKTypography>
                          <SKTypography
                            variant="body2"
                            style={{
                              fontWeight: 600,
                              color: moment().isAfter(moment(invoice.active.external.expiryDate))
                                ? "#d32f2f"
                                : "inherit",
                            }}
                          >
                            {moment(invoice?.active?.external?.expiryDate).format("LLL")}
                          </SKTypography>
                        </Grid>
                      )}
                    </Grid>

                    <Box className={classes.invoiceDetailList}>
                      <Box className={classes.detailRow}>
                        <SKTypography className={classes.detailLabel}>{t("Subtotal")}</SKTypography>
                        <SKTypography className={classes.detailValue}>
                          {formatNumber(invoice?.active.amount?.subtotal || invoice?.active.amount?.total, "currency")}
                        </SKTypography>
                      </Box>
                      {/* <Box className={classes.detailRow}>
                        <SKTypography className={classes.detailLabel}>{t("Tax")}</SKTypography>
                        <SKTypography className={classes.detailValue}>
                          {formatNumber(invoice?.active.amount.tax, "currency")}
                        </SKTypography>
                      </Box> */}
                      <Box className={classes.detailRow}>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                          <SKTypography className={classes.detailLabel}>{t("Voucher")}</SKTypography>
                          {/* TODO: need to ensure backend is not buggy */}
                          {invoice?.active.discountItems?.length === 0 && (
                            <>
                              <SKButton
                                variant="text"
                                size="small"
                                onClick={() => setShowModalDiscount(true)}
                                style={{ minWidth: "auto", padding: "2px 2px", fontSize: "0.6rem", color: "green" }}
                              >
                                {t("Add")}
                              </SKButton>

                              <Dialog
                                open={showModalDiscount}
                                onClose={() => !isApplyingDiscount && setShowModalDiscount(false)}
                              >
                                <DialogTitle>{t("Apply Billing Code")}</DialogTitle>
                                <DialogContent>
                                  <TextField
                                    autoFocus
                                    margin="dense"
                                    label={t("Billing Code")}
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    value={discountCode}
                                    onChange={(e) => setDiscountCode(e.target.value)}
                                  />
                                </DialogContent>
                                <DialogActions>
                                  <Button
                                    onClick={() => setShowModalDiscount(false)}
                                    color="primary"
                                    disabled={isApplyingDiscount}
                                  >
                                    {t("Cancel")}
                                  </Button>
                                  <Button
                                    onClick={handleApplyDiscount}
                                    color="primary"
                                    variant="contained"
                                    disabled={isApplyingDiscount || !discountCode.trim()}
                                  >
                                    {t("Apply")}
                                  </Button>
                                </DialogActions>
                              </Dialog>
                            </>
                          )}
                        </Box>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                          {/* TODO: Need to ensure backend is not buggy  */}
                          {invoice?.active.discountItems?.length > 0 && (
                            <SKButton
                              size="small"
                              color="secondary"
                              onClick={handleRemoveDiscount}
                              style={{ minWidth: "auto", padding: "2px 4px", fontSize: "0.6rem" }}
                            >
                              {t("Remove")}
                            </SKButton>
                          )}
                          <SKTypography className={classes.detailValue}>
                            {formatNumber(invoice?.active.amount.discount, "currency")}
                          </SKTypography>
                        </Box>
                      </Box>
                      <Box className={`${classes.detailRow} ${classes.totalRow}`}>
                        <SKTypography className={classes.totalLabel}>{t("Total Amount")}</SKTypography>
                        <SKTypography className={classes.totalValue}>
                          {invoice?.active.formattedAmount || formatNumber(invoice?.active.amount?.total, "currency")}
                        </SKTypography>
                      </Box>
                    </Box>

                    <Box mt={2.5} display="flex" alignItems="center" justifyContent="flex-end" style={{ gap: 8 }}>
                      {missingPaymentLink && (
                        <SKButton
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={handleRegeneratePaymentLink}
                          disabled={isGenerating}
                        >
                          {isGenerating ? t("Generating...") : t("Regenerate Payment Link")}
                        </SKButton>
                      )}
                      {invoice?.active.external?.paymentUrl && (
                        <SKButton
                          variant="contained"
                          color="primary"
                          size="small"
                          endIcon={<OpenInNew fontSize="small" />}
                          disabled={moment().isAfter(moment(invoice.active.external.expiryDate))}
                          onClick={() => window.open(invoice?.active.external.paymentUrl, "_blank")}
                        >
                          {t("Pay Now")}
                        </SKButton>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box textAlign="center" py={2}>
                    <NoDataFound
                      title={"No Active Billing"}
                      description={"You don't have any pending payments at the moment."}
                      variant="small"
                    />
                  </Box>
                )}
              </SKCardContent>
            </SKCard>
          </Grid>

          <Grid item xs={12} md={12}>
            {/* TODO TASK 1:  Only show 5 latest billing/invoices, create button view all to redirect to subscription invoices page*/}
            <SKCard variant="static">
              <SKCardHeader
                title={t("Billing History")}
                action={
                  <Box>
                    <SKButton
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => router.push("/subscription/invoices?returnTab=billing")}
                    >
                      {t("View All")}
                    </SKButton>
                  </Box>
                }
              />
              <SKCardContent style={{ paddingBlock: 4 }}>
                {invoice.list.length > 0 ? (
                  <>
                    <SKList>
                      {invoice.list.slice(0, 5).map((inv) => (
                        <SKListItem
                          key={inv.id}
                          icon={<Receipt style={{ fontSize: "1rem", color: "rgba(0,0,0,0.4)" }} />}
                          label={
                            <Box>
                              <SKTypography variant="body2" style={{ fontWeight: 700 }}>
                                {inv.invoiceNumber}
                              </SKTypography>
                              <SKTypography variant="caption" subVariant="caption">
                                {moment(inv.createdAt).format("LL")}
                              </SKTypography>
                            </Box>
                          }
                          value={
                            <Box display="flex" flexDirection="column" alignItems="flex-end" style={{ gap: 3 }}>
                              <SKChip
                                label={capitalizeFirstLetter(inv.status)}
                                type={inv.status === "paid" ? "success" : inv.status === "void" ? "void" : "pending"}
                                style={{ minWidth: 60 }}
                              />
                              {/* <SKTypography variant="body2" style={{ fontWeight: 600 }}>
                                {formatNumber(inv.amount.total, "currency")}
                              </SKTypography> */}
                            </Box>
                          }
                        />
                      ))}
                    </SKList>
                  </>
                ) : (
                  <Box textAlign="center" py={4}>
                    <NoDataFound
                      title={"No Billing History"}
                      description={"Your billing history will appear here once you have made payments."}
                      variant="small"
                    />
                  </Box>
                )}
              </SKCardContent>
            </SKCard>
          </Grid>
        </Grid>
      </Box>
    </React.Fragment>
  );
};

export default observer(BillingPanel);
